<script setup lang="ts">
/**
 * Module: web/src/components/entries/EntryDetailModal.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.1
 * Date: 2026-07-19
 *
 * v1.1.1 (aria-required rider, b689b5e follow-up, S22): the asterisk-marked required controls now carry aria-required="true" (native inputs/selects by id or label wrap; the Customer-type radiogroup at group level; ReasonGroupSelect self-announces and stays untouched).
 *
 * v1.1.0 (required-marking polarity flip -- Robert, S20; DispatchView
 * 2.7.0 parity): required edit-form fields carry the accent asterisk +
 * the "* required" legend; the form grid gains items-end so a wrapped
 * label can never misalign its input against its row partner.
 *
 * The shared Entry Detail modal (contract entry-log-api v1.0.0 SS4; AE
 * slice 4, decision #176) -- used by EntryLogView (any row) and
 * MyEntriesView (own rows). Backend = the EXISTING pair only:
 * GET /api/mo-entries/{id} (edit-view read) + PUT /api/mo-entries/{id}
 * (same-validation full-body edit). No new write endpoints; no BE lock.
 *
 * Contract behaviors carried here:
 *   - READ-FIRST + RELOCK-PER-OPEN: every open starts in read mode; the
 *     modal never remembers edit mode across opens.
 *   - PERMISSION-AWARE UNLOCK: the GET's own gate (data_entry +
 *     own-or-manager) decides -- a successful read means the PUT is allowed
 *     (identical server rule), so Unlock renders whenever the detail loaded
 *     and the row is not voided. When the read 403s (a listing can show
 *     rows the caller cannot open -- e.g. a per-user entry_log_view grant
 *     without manager tier), the ROW's own fields render as a read-only
 *     summary with an honest note and no Unlock.
 *   - FE SOFT LOCK + PILL: the LOCKED / EDITING pill is presentation state
 *     only -- last-write-wins is accepted; the revision trail keeps every
 *     version.
 *   - DIRTY-CLOSE GUARD: X / backdrop / Escape all route through one close
 *     handler; unsaved changes ask before discarding.
 *   - VOIDED: pill + narration + pointer at Audit Log undo; never a raw
 *     error (the PUT would 409 {code:'entry_voided'} -- we don't offer it).
 *   - SAVE: PUT full body -> per-field 422 rendering -> refresh + relock in
 *     place (stay open showing the saved state); emits 'saved' so the host
 *     list refreshes.
 */
import { computed, ref, watch } from 'vue';
import { apiGet, apiPut, ApiError } from '@/composables/useApi';
import { useAuthStore } from '@/stores/auth';
import type { MoEntryDetail, MoFormBootstrap } from '@/types/api';
import AppIcon from '@/components/AppIcon.vue';
import BaseModal from '@/components/BaseModal.vue';
import ReasonGroupSelect from '@/components/entry/ReasonGroupSelect.vue';
import { fmtDateTime } from '@/lib/format';

const props = defineProps<{
  open: boolean;
  entryId: number | null;
  /**
   * Optional list-row fallback (the 403 path): loose display fields the
   * host list already has -- rendered when the full read is forbidden.
   */
  row?: {
    call_date?: string | null;
    call_time?: string | null;
    dispatcher_name?: string | null;
    caller_phone?: string | null;
    caller_company?: string | null;
    quote_given?: number | string | null;
    reasons?: string[];
    source?: string;
    voided_at?: string | null;
  } | null;
}>();

const emit = defineEmits<{ (e: 'close'): void; (e: 'saved', id: number): void }>();

const auth = useAuthStore();

// ---- load state ---------------------------------------------------------------
const detail = ref<MoEntryDetail | null>(null);
const loadState = ref<'idle' | 'loading' | 'ready' | 'forbidden' | 'error'>('idle');
const bootstrap = ref<MoFormBootstrap | null>(null);

// ---- mode + form ----------------------------------------------------------------
const mode = ref<'read' | 'edit'>('read');
const saving = ref(false);
const saveError = ref('');
const fieldErrors = ref<Record<string, string>>({});
const confirmDiscard = ref(false);
/** Where the discard confirm came from: closing the modal vs Cancel->read. */
const discardTarget = ref<'close' | 'relock'>('close');

interface EntryForm {
  dispatcher_person_id: number | null;
  call_date: string;
  call_time: string;
  customer_type: string;
  account_id: number | null;
  caller_company: string;
  caller_name: string;
  caller_phone: string;
  caller_location: string;
  pickup_lat: string;
  pickup_lon: string;
  duty_type_id: number | null;
  service_type_id: number | null;
  eta_minutes: string;
  mileage: string;
  quote_given: string;
  quote_note: string;
  competitor_quote: string;
  competitor_note: string;
  callback_potential: string;
  notes: string;
  reasons: number[];
}
const form = ref<EntryForm | null>(null);
const baseline = ref('');

const dirty = computed(() =>
  mode.value === 'edit' && form.value !== null
  && JSON.stringify(form.value) !== baseline.value);

const voided = computed(() =>
  detail.value?.voided_at != null || (loadState.value === 'forbidden' && props.row?.voided_at != null));

/** GET success == PUT allowed (identical server gate), so Unlock needs only
 *  a loaded, un-voided row. */
const canEdit = computed(() =>
  loadState.value === 'ready' && !voided.value);

// ---- open/close lifecycle -------------------------------------------------------
watch(
  () => [props.open, props.entryId] as const,
  ([open, id]) => {
    if (!open || id === null) return;
    // relock-per-open: read mode, clean slate, fresh fetch
    mode.value = 'read';
    form.value = null;
    saveError.value = '';
    fieldErrors.value = {};
    confirmDiscard.value = false;
    void load(id);
  },
);

async function load(id: number) {
  loadState.value = 'loading';
  detail.value = null;
  try {
    detail.value = await apiGet<MoEntryDetail>(`/mo-entries/${id}`);
    loadState.value = 'ready';
    // vocabularies resolve read-mode labels too -- fetch once per session
    // (the endpoint is private-cached 5 min; data_entry holders only).
    if (!bootstrap.value && auth.hasCap('data_entry')) {
      try {
        bootstrap.value = await apiGet<MoFormBootstrap>('/mo-form/bootstrap');
      } catch { /* labels degrade to ids -- non-fatal */ }
    }
  } catch (e) {
    loadState.value = e instanceof ApiError && e.status === 403 ? 'forbidden' : 'error';
  }
}

/** ONE close funnel: BaseModal's X/backdrop/Esc all land here. */
function requestClose() {
  if (dirty.value && !confirmDiscard.value) {
    discardTarget.value = 'close';
    confirmDiscard.value = true;
    return;
  }
  confirmDiscard.value = false;
  emit('close');
}

/** The confirm bar's Discard: honor the intent that raised it. */
function confirmDiscardAction() {
  confirmDiscard.value = false;
  form.value = null;
  mode.value = 'read';
  if (discardTarget.value === 'close') emit('close');
}

// ---- unlock -> edit -------------------------------------------------------------
async function unlock() {
  const d = detail.value;
  if (!d || !canEdit.value) return;
  // The edit form needs the vocabularies -- ensure they arrived (the load()
  // fetch is fire-and-forget-tolerant; here they are load-bearing).
  if (!bootstrap.value && auth.hasCap('data_entry')) {
    try {
      bootstrap.value = await apiGet<MoFormBootstrap>('/mo-form/bootstrap');
    } catch { /* the template renders the loading hint; save still validates server-side */ }
  }
  form.value = {
    dispatcher_person_id: d.dispatcher_person_id,
    call_date: d.call_date ?? '',
    call_time: (d.call_time ?? '').slice(0, 5),
    customer_type: d.customer_type ?? 'UNKNOWN',
    account_id: d.account_id,
    caller_company: d.caller_company ?? '',
    caller_name: d.caller_name ?? '',
    caller_phone: d.caller_phone ?? '',
    caller_location: d.caller_location ?? '',
    pickup_lat: d.pickup_lat != null ? String(d.pickup_lat) : '',
    pickup_lon: d.pickup_lon != null ? String(d.pickup_lon) : '',
    duty_type_id: d.duty_type_id,
    service_type_id: d.service_type_id,
    eta_minutes: d.eta_minutes != null ? String(d.eta_minutes) : '',
    mileage: d.mileage != null ? String(d.mileage) : '',
    quote_given: d.quote_given != null ? String(d.quote_given) : '',
    quote_note: d.quote_note ?? '',
    competitor_quote: d.competitor_quote != null ? String(d.competitor_quote) : '',
    competitor_note: d.competitor_note ?? '',
    callback_potential: d.callback_potential != null ? String(d.callback_potential) : '',
    notes: d.notes ?? '',
    reasons: [...d.reasons],
  };
  baseline.value = JSON.stringify(form.value);
  saveError.value = '';
  fieldErrors.value = {};
  mode.value = 'edit';
}

function relock() {
  if (dirty.value) {
    discardTarget.value = 'relock';
    confirmDiscard.value = true;
    return;
  }
  form.value = null;
  mode.value = 'read';
}

// ---- save -----------------------------------------------------------------------
function numOrNull(s: string): string | null {
  const t = s.trim();
  return t === '' ? null : t;
}

async function save() {
  const f = form.value;
  const d = detail.value;
  if (!f || !d || saving.value) return;
  saving.value = true;
  saveError.value = '';
  fieldErrors.value = {};
  try {
    // Full body -- the PUT validates exactly like create (doc 05 SS9); the
    // server derives shift + goto_call_id/transcript_ref from time +
    // recording_id, so those never ride the form.
    await apiPut(`/mo-entries/${d.id}`, {
      dispatcher_person_id: f.dispatcher_person_id,
      call_date: f.call_date,
      call_time: f.call_time,
      customer_type: f.customer_type,
      account_id: f.customer_type === 'ACCOUNT' ? f.account_id : null,
      caller_company: f.caller_company || null,
      caller_name: f.caller_name || null,
      caller_phone: f.caller_phone || null,
      caller_location: f.caller_location || null,
      pickup_lat: numOrNull(f.pickup_lat),
      pickup_lon: numOrNull(f.pickup_lon),
      pickup_canonical_address: d.pickup_canonical_address,
      pickup_geocode_confidence: d.pickup_geocode_confidence,
      pickup_place_id: d.pickup_place_id,
      duty_type_id: f.duty_type_id,
      service_type_id: f.service_type_id,
      eta_minutes: numOrNull(f.eta_minutes),
      eta_raw: d.eta_raw,
      mileage: numOrNull(f.mileage),
      quote_given: numOrNull(f.quote_given),
      quote_note: f.quote_note || null,
      competitor_quote: numOrNull(f.competitor_quote),
      competitor_note: f.competitor_note || null,
      callback_potential: numOrNull(f.callback_potential),
      notes: f.notes || null,
      recording_id: d.recording_id,
      reasons: f.reasons,
    });
    emit('saved', d.id);
    form.value = null;
    mode.value = 'read';
    await load(d.id); // show the saved state, relocked
  } catch (e) {
    if (e instanceof ApiError && e.status === 422) {
      const body = e.body as { errors?: Record<string, string> } | null;
      fieldErrors.value = body?.errors ?? {};
      saveError.value = 'Some fields need attention -- see below.';
    } else if (e instanceof ApiError && e.code === 'entry_voided') {
      saveError.value = 'This entry was voided while you were editing. Undo the void from the Audit Log before editing.';
    } else if (e instanceof ApiError && e.code === 'duplicate_call') {
      saveError.value = 'Another entry already claims this call -- the linked call can only back one committed entry.';
    } else {
      saveError.value = 'Could not save the entry. Try again.';
    }
  } finally {
    saving.value = false;
  }
}

// ---- read-mode label resolution -------------------------------------------------
const accountLabel = computed(() => {
  const id = detail.value?.account_id;
  if (id == null) return null;
  return bootstrap.value?.accounts.find((a) => a.id === id)?.label ?? `#${id}`;
});
const dutyLabel = computed(() => {
  const id = detail.value?.duty_type_id;
  if (id == null) return null;
  return bootstrap.value?.duty_types.find((x) => x.id === id)?.code ?? `#${id}`;
});
const serviceLabel = computed(() => {
  const id = detail.value?.service_type_id;
  if (id == null) return null;
  return bootstrap.value?.service_types.find((x) => x.id === id)?.code ?? `#${id}`;
});
const dispatcherLabel = computed(() => {
  const id = detail.value?.dispatcher_person_id;
  if (id == null) return null;
  const fromBootstrap = bootstrap.value?.people?.find((p) => p.id === id)?.full_name;
  if (fromBootstrap) return fromBootstrap;
  if (id === auth.user?.person_id) return auth.user?.name ?? `#${id}`;
  return props.row?.dispatcher_name ?? `#${id}`;
});
const reasonCodes = computed(() => {
  const ids = detail.value?.reasons ?? [];
  const all = bootstrap.value ? Object.values(bootstrap.value.reasons).flat() : [];
  return ids.map((id) => all.find((r) => r.id === id)?.code ?? `#${id}`);
});

function fmtMoney(v: number | string | null | undefined): string {
  if (v == null || v === '') return '—';
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n)
    ? n.toLocaleString('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
    : String(v);
}

const title = computed(() =>
  props.entryId != null ? `Entry #${props.entryId}` : 'Entry');
</script>

<template>
  <BaseModal :open="open" :title="title" max-width="md:max-w-3xl" @close="requestClose">
    <!-- state pill row -->
    <div class="flex items-center gap-2 flex-wrap mb-4">
      <span v-if="voided" class="chip chip-bad">VOIDED</span>
      <template v-else>
        <span v-if="mode === 'read'" class="chip chip-neutral">
          <AppIcon name="key" class="w-3.5 h-3.5" /> Locked
        </span>
        <span v-else class="chip chip-good">Editing</span>
      </template>
      <span v-if="detail?.source" class="tag-mini">{{ detail.source }}</span>
      <span v-else-if="loadState === 'forbidden' && row?.source" class="tag-mini">{{ row.source }}</span>
      <span v-if="detail?.submitted_at" class="text-xs text-fg-muted">
        submitted {{ fmtDateTime(detail.submitted_at) }}
      </span>
    </div>

    <!-- voided narration (contract SS4: a state we explain, never an error) -->
    <div v-if="voided" class="rounded-lg border border-border-c bg-input/50 p-3 mb-4 text-sm text-fg-muted">
      This entry is voided -- it no longer appears in reports or coverage.
      To edit it, undo the void first from
      <span class="text-fg font-medium">Administration → Audit Log</span>.
    </div>

    <!-- loading -->
    <div v-if="loadState === 'loading'" class="space-y-2" aria-label="Loading entry">
      <div class="skeleton h-4 w-2/3" />
      <div class="skeleton h-4 w-full" />
      <div class="skeleton h-4 w-1/2" />
    </div>

    <!-- hard error -->
    <div v-else-if="loadState === 'error'" class="alert-error" role="alert">
      Could not load the entry. Close and try again.
    </div>

    <!-- 403 fallback: the row's own fields, honestly labeled -->
    <template v-else-if="loadState === 'forbidden'">
      <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt class="text-fg-muted">Date</dt><dd class="text-fg text-right">{{ row?.call_date ?? '—' }}</dd>
        <dt class="text-fg-muted">Time</dt><dd class="text-fg text-right">{{ (row?.call_time ?? '').slice(0, 5) || '—' }}</dd>
        <dt class="text-fg-muted">Dispatcher</dt><dd class="text-fg text-right">{{ row?.dispatcher_name ?? '—' }}</dd>
        <dt class="text-fg-muted">Phone</dt><dd class="text-fg text-right tabular-nums">{{ row?.caller_phone ?? '—' }}</dd>
        <dt class="text-fg-muted">Company</dt><dd class="text-fg text-right">{{ row?.caller_company ?? '—' }}</dd>
        <dt class="text-fg-muted">Quote</dt><dd class="text-fg text-right tabular-nums">{{ fmtMoney(row?.quote_given) }}</dd>
      </dl>
      <div v-if="row?.reasons?.length" class="mt-3 flex flex-wrap gap-1.5">
        <span v-for="r in row.reasons" :key="r" class="chip chip-neutral">{{ r }}</span>
      </div>
      <p class="mt-4 text-xs text-fg-muted italic">
        Summary view -- opening the full entry requires manager access.
      </p>
    </template>

    <!-- READ mode -->
    <template v-else-if="loadState === 'ready' && detail && mode === 'read'">
      <dl class="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <dt class="text-fg-muted">Date</dt><dd class="text-fg text-right">{{ detail.call_date ?? '—' }}</dd>
        <dt class="text-fg-muted">Time</dt><dd class="text-fg text-right">{{ (detail.call_time ?? '').slice(0, 5) || '—' }} <span v-if="detail.shift" class="text-fg-muted">· {{ detail.shift }}</span></dd>
        <dt class="text-fg-muted">Dispatcher</dt><dd class="text-fg text-right">{{ dispatcherLabel ?? '—' }}</dd>
        <dt class="text-fg-muted">Customer type</dt><dd class="text-fg text-right">{{ detail.customer_type ?? '—' }}</dd>
        <dt class="text-fg-muted">Account</dt><dd class="text-fg text-right">{{ accountLabel ?? '—' }}</dd>
        <dt class="text-fg-muted">Company</dt><dd class="text-fg text-right">{{ detail.caller_company ?? '—' }}</dd>
        <dt class="text-fg-muted">Caller</dt><dd class="text-fg text-right">{{ detail.caller_name ?? '—' }}</dd>
        <dt class="text-fg-muted">Phone</dt><dd class="text-fg text-right tabular-nums">{{ detail.caller_phone ?? '—' }}</dd>
        <dt class="text-fg-muted">Location</dt><dd class="text-fg text-right">{{ detail.caller_location ?? detail.pickup_canonical_address ?? '—' }}</dd>
        <dt class="text-fg-muted">Duty</dt><dd class="text-fg text-right">{{ dutyLabel ?? '—' }}</dd>
        <dt class="text-fg-muted">Service</dt><dd class="text-fg text-right">{{ serviceLabel ?? '—' }}</dd>
        <dt class="text-fg-muted">ETA given</dt><dd class="text-fg text-right">{{ detail.eta_minutes != null ? `${detail.eta_minutes} min` : '—' }}</dd>
        <dt class="text-fg-muted">Mileage</dt><dd class="text-fg text-right tabular-nums">{{ detail.mileage ?? '—' }}</dd>
        <dt class="text-fg-muted">Quote given</dt><dd class="text-fg text-right tabular-nums">{{ fmtMoney(detail.quote_given) }}</dd>
        <dt v-if="detail.quote_note" class="text-fg-muted">Quote note</dt><dd v-if="detail.quote_note" class="text-fg text-right">{{ detail.quote_note }}</dd>
        <dt class="text-fg-muted">Competitor quote</dt><dd class="text-fg text-right tabular-nums">{{ fmtMoney(detail.competitor_quote) }}</dd>
        <dt v-if="detail.competitor_note" class="text-fg-muted">Competitor note</dt><dd v-if="detail.competitor_note" class="text-fg text-right">{{ detail.competitor_note }}</dd>
        <dt class="text-fg-muted">Callback potential</dt><dd class="text-fg text-right">{{ detail.callback_potential ?? '—' }}</dd>
      </dl>
      <div v-if="reasonCodes.length" class="mt-3 flex flex-wrap gap-1.5">
        <span v-for="r in reasonCodes" :key="r" class="chip chip-neutral">{{ r }}</span>
      </div>
      <p v-if="detail.notes" class="mt-3 text-sm text-fg whitespace-pre-wrap border-t border-border-c/60 pt-3">{{ detail.notes }}</p>
      <p v-if="detail.recording_id" class="mt-3 text-xs text-fg-muted">
        Linked to call recording <span class="font-mono">{{ detail.recording_id }}</span>
      </p>
    </template>

    <!-- EDIT mode -->
    <template v-else-if="loadState === 'ready' && detail && mode === 'edit' && form">
      <div v-if="saveError" class="alert-error mb-3" role="alert">{{ saveError }}</div>
      <p class="text-xs text-fg-muted mb-3">
        Fields marked <span class="text-accent">*</span> are required.
      </p>
      <div class="grid grid-cols-1 md:grid-cols-2 gap-3 items-end">
        <label class="block">
          <span class="field-label">Date <span class="text-accent" aria-hidden="true">*</span></span>
          <input aria-required="true" v-model="form.call_date" type="date" class="field-input" :class="fieldErrors.call_date ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.call_date" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.call_date }}</span>
        </label>
        <label class="block">
          <span class="field-label">Time <span class="text-accent" aria-hidden="true">*</span></span>
          <input aria-required="true" v-model="form.call_time" type="time" class="field-input" :class="fieldErrors.call_time ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.call_time" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.call_time }}</span>
        </label>
        <label v-if="bootstrap?.people" class="block">
          <span class="field-label">Dispatcher (manager override)</span>
          <select v-model.number="form.dispatcher_person_id" class="field-input">
            <option v-for="p in bootstrap.people" :key="p.id" :value="p.id">{{ p.full_name }}</option>
          </select>
          <span v-if="fieldErrors.dispatcher_person_id" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.dispatcher_person_id }}</span>
        </label>
        <label class="block">
          <span class="field-label">Customer type <span class="text-accent" aria-hidden="true">*</span></span>
          <select aria-required="true" v-model="form.customer_type" class="field-input" :class="fieldErrors.customer_type ? '!border-accent-bad' : ''">
            <option value="NON_ACCOUNT">Non-account</option>
            <option value="ACCOUNT">Account</option>
            <option value="UNKNOWN">Unknown</option>
          </select>
          <span v-if="fieldErrors.customer_type" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.customer_type }}</span>
        </label>
        <label v-if="form.customer_type === 'ACCOUNT'" class="block md:col-span-2">
          <span class="field-label">Account <span class="text-accent" aria-hidden="true">*</span></span>
          <select aria-required="true" v-model.number="form.account_id" class="field-input" :class="fieldErrors.account_id ? '!border-accent-bad' : ''">
            <option :value="null">— pick an account —</option>
            <option v-for="a in bootstrap?.accounts ?? []" :key="a.id" :value="a.id">{{ a.label }}</option>
          </select>
          <span v-if="fieldErrors.account_id" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.account_id }}</span>
        </label>
        <label class="block">
          <span class="field-label">Company</span>
          <input v-model="form.caller_company" type="text" class="field-input" />
        </label>
        <label class="block">
          <span class="field-label">Caller name</span>
          <input v-model="form.caller_name" type="text" class="field-input" />
        </label>
        <label class="block">
          <span class="field-label">Phone</span>
          <input v-model="form.caller_phone" type="tel" class="field-input" :class="fieldErrors.caller_phone ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.caller_phone" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.caller_phone }}</span>
        </label>
        <label class="block">
          <span class="field-label">Location (as told)</span>
          <input v-model="form.caller_location" type="text" class="field-input" />
        </label>
        <label class="block">
          <span class="field-label">Duty <span class="text-accent" aria-hidden="true">*</span></span>
          <select aria-required="true" v-model.number="form.duty_type_id" class="field-input" :class="fieldErrors.duty_type_id ? '!border-accent-bad' : ''">
            <option :value="null">— pick —</option>
            <option v-for="d in bootstrap?.duty_types ?? []" :key="d.id" :value="d.id">{{ d.code }}</option>
          </select>
          <span v-if="fieldErrors.duty_type_id" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.duty_type_id }}</span>
        </label>
        <label class="block">
          <span class="field-label">Service <span class="text-accent" aria-hidden="true">*</span></span>
          <select aria-required="true" v-model.number="form.service_type_id" class="field-input" :class="fieldErrors.service_type_id ? '!border-accent-bad' : ''">
            <option :value="null">— pick —</option>
            <option v-for="s in bootstrap?.service_types ?? []" :key="s.id" :value="s.id">{{ s.code }}</option>
          </select>
          <span v-if="fieldErrors.service_type_id" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.service_type_id }}</span>
        </label>
        <label class="block">
          <span class="field-label">ETA given (minutes)</span>
          <input v-model="form.eta_minutes" type="text" inputmode="numeric" class="field-input" :class="fieldErrors.eta_minutes ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.eta_minutes" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.eta_minutes }}</span>
        </label>
        <label class="block">
          <span class="field-label">Mileage</span>
          <input v-model="form.mileage" type="text" inputmode="decimal" class="field-input" :class="fieldErrors.mileage ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.mileage" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.mileage }}</span>
        </label>
        <label class="block">
          <span class="field-label">Quote given ($)</span>
          <input v-model="form.quote_given" type="text" inputmode="decimal" class="field-input" :class="fieldErrors.quote_given ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.quote_given" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.quote_given }}</span>
        </label>
        <label class="block">
          <span class="field-label">Quote note</span>
          <input v-model="form.quote_note" type="text" class="field-input" />
        </label>
        <label class="block">
          <span class="field-label">Competitor quote ($)</span>
          <input v-model="form.competitor_quote" type="text" inputmode="decimal" class="field-input" :class="fieldErrors.competitor_quote ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.competitor_quote" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.competitor_quote }}</span>
        </label>
        <label class="block">
          <span class="field-label">Competitor note</span>
          <input v-model="form.competitor_note" type="text" class="field-input" />
        </label>
        <label class="block">
          <span class="field-label">Callback potential (1-10)</span>
          <input v-model="form.callback_potential" type="text" inputmode="numeric" class="field-input" :class="fieldErrors.callback_potential ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.callback_potential" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.callback_potential }}</span>
        </label>
        <label class="block">
          <span class="field-label">Pickup lat</span>
          <input v-model="form.pickup_lat" type="text" inputmode="decimal" class="field-input" :class="fieldErrors.pickup_lat || fieldErrors.pickup ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.pickup_lat" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.pickup_lat }}</span>
        </label>
        <label class="block">
          <span class="field-label">Pickup lon</span>
          <input v-model="form.pickup_lon" type="text" inputmode="decimal" class="field-input" :class="fieldErrors.pickup_lon || fieldErrors.pickup ? '!border-accent-bad' : ''" />
          <span v-if="fieldErrors.pickup_lon" class="mt-1 text-xs text-accent-bad block">{{ fieldErrors.pickup_lon }}</span>
        </label>
        <span v-if="fieldErrors.pickup" class="mt-1 text-xs text-accent-bad block md:col-span-2">{{ fieldErrors.pickup }}</span>
        <label class="block md:col-span-2">
          <span class="field-label">Notes</span>
          <textarea v-model="form.notes" rows="3" class="field-input !h-auto" />
        </label>
        <div class="md:col-span-2">
          <ReasonGroupSelect
            v-if="bootstrap"
            v-model="form.reasons"
            :groups="bootstrap.reasons"
            :error="fieldErrors.reasons ?? null"
          />
          <span v-else class="text-sm text-fg-muted">Loading reason vocabulary…</span>
        </div>
      </div>
    </template>

    <template #footer>
      <!-- dirty-close confirm bar (one guard for X/backdrop/Esc/relock) -->
      <div v-if="confirmDiscard" class="flex items-center justify-between gap-3 flex-wrap">
        <span class="text-sm text-fg">Discard unsaved changes?</span>
        <div class="flex gap-2">
          <button class="btn-secondary !h-9 !px-3 text-sm" @click="confirmDiscard = false">Keep editing</button>
          <button class="btn-primary !h-9 !px-3 text-sm !bg-accent-warm" @click="confirmDiscardAction">Discard</button>
        </div>
      </div>
      <div v-else class="flex items-center justify-end gap-2 flex-wrap">
        <template v-if="mode === 'read'">
          <button class="btn-secondary !h-10 !px-4 text-sm" @click="requestClose">Close</button>
          <button v-if="canEdit" class="btn-primary !h-10 !px-4 text-sm" @click="unlock">
            <AppIcon name="key" class="w-4 h-4" /> Unlock to edit
          </button>
        </template>
        <template v-else>
          <button class="btn-secondary !h-10 !px-4 text-sm" :disabled="saving" @click="relock">Cancel</button>
          <button class="btn-primary !h-10 !px-4 text-sm" :disabled="saving || !dirty" @click="save">
            {{ saving ? 'Saving…' : 'Save changes' }}
          </button>
        </template>
      </div>
    </template>
  </BaseModal>
</template>
